import { Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { groupBy, uniq, uniqBy } from 'lodash';
import { combineLatest, filter, forkJoin, Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { ButtonStyles } from '../../button/button.component';
import { DirectoryNumberResource } from '../../self-serve/resources/directory-number.resource';
import { SearchDirectoryNumberResource } from '../../self-serve/resources/search/search-directory-number.resource';
import { SearchExtensionMobilityProfilesResource } from '../../self-serve/resources/search/search-extension-mobility-profiles.resource';
import { SnrProfileResource } from '../../self-serve/resources/snr-profile.resource';
import { AuthenticationContext } from '../../shared/contexts/authentication.context';
import { CurrentClusterContext } from '../../shared/contexts/current-cluster.context';
import { ServiceTypes } from '../../shared/contexts/global-360-view.context';
import { GlobalPropertiesContext } from '../../shared/contexts/global-properties.context';
import { MicrosoftTenantsStateContext } from '../../shared/contexts/microsoft-tenants-state.context';
import { SearchFieldConfigContext } from '../../shared/contexts/search-field-config.context';
import { SiteSummaryContext } from '../../shared/contexts/site-summary.context';
import { ZpmUserPhotoContext } from '../../shared/contexts/zpm-user-photo.context';
import {
  ClusterResult,
  CurrentUser,
  DevicePoolResult,
  DirectoryNumberResult,
  DistributionListResult,
  EmergencyLocationsSearchResults,
  EndUserResult,
  ExtensionMobilityResult,
  Microsoft365UserRef,
  Microsoft365UserResult,
  MicrosoftTeamsAutoAttendant,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsUserResult,
  PhoneResult,
  Role,
  SearchFieldConfig,
  SiteResult,
  SiteSummary,
  SnrProfileResult,
  VoicemailResult,
  ZpmPrivilege,
} from '../../shared/models/generated/ziroModels';
import { Nvp } from '../../shared/models/nvp';
import { ZiroIcons, ZiroStackedIcons } from '../../shared/models/ziro-icons.enum';
import { EndUserSearchResource } from '../../shared/resources/end-user-search.resource';
import { PhoneResource } from '../../shared/resources/phone.resource';
import { SearchPhoneResource } from '../../shared/resources/search-phone.resource';
import { SearchResource } from '../../shared/resources/search.resource';
import { VoicemailSearchResource } from '../../shared/resources/voicemail-search.resource';
import { VoicemailResource } from '../../shared/resources/voicemail.resource';
import { ZiroModalService } from '../../shared/services/ziro-modal.service';
import { ToastService } from '../../shared/services/toast.service';
import { KnownPhoneModels, PhoneDescriptions } from '../shared/contexts/user-detail-ui.context';
import { SearchFieldConfigResource } from '../shared/resources/field-config/search-field-config.resource';
import { Microsoft365UserResource } from '../shared/resources/microsoft-365-user.resource';
import { MicrosoftTeamsUserSearchResource } from '../shared/resources/microsoft-teams-user-search.resource';
import { MicrosoftUserUiResult } from './microsoft-search-result/microsoft-search-result.component';
import { MicrosoftCallQueueSearchResource } from '../shared/resources/microsoft-call-queue-search.resource';
import { EmergencyLocationSearchResource } from '../shared/resources/emergency-location-search.resource';
import { BreadcrumbsService } from '../../shared/breadcrumbs/breadcrumbs.service';
import { getUserInitialsFromDisplayName } from '../../shared/utils';
import { MicrosoftAutoAttendantsSearchResource } from '../shared/resources/microsoft-auto-attendants-search.resource';

export interface DevicePoolSites {
  [devicePool: string]: SiteResult[];
}

export interface UiEndUserResult extends EndUserResult {
  isDisabled?: boolean;
  isSyncInProgress?: boolean;
  tooltip?: string;
}

interface EditLinkState {
  isDisabled: boolean;
  disabledTooltip: string;
}

interface ZpmResultsLoadingState {
  microsoftEndUserResultsLoading: WritableSignal<boolean>;
  callQueueResultsLoading: WritableSignal<boolean>;
  emergencyLocationResultsLoading: WritableSignal<boolean>;
  autoAttendantResultsLoading: WritableSignal<boolean>;
}

interface LocationPlaceSearchResult {
  id: string;
  name: string;
  address: string;
  link: string;
  location?: string;
}

@Component({
  selector: 'ziro-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  providers: [
    EndUserSearchResource,
    SearchPhoneResource,
    VoicemailSearchResource,
    SearchResource,
    SearchDirectoryNumberResource,
    PhoneResource,
    DirectoryNumberResource,
    VoicemailResource,
    SnrProfileResource,
    SearchFieldConfigResource,
    EmergencyLocationSearchResource,
  ],
  standalone: false,
})
export class ZiroSearchComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private route = inject(ActivatedRoute);
  private authenticationContext = inject(AuthenticationContext);
  private searchResource = inject(SearchResource);
  private endUserSearchResource = inject(EndUserSearchResource);
  private voicemailSearchResource = inject(VoicemailSearchResource);
  private searchDirectoryNumberResource = inject(SearchDirectoryNumberResource);
  private searchPhoneResource = inject(SearchPhoneResource);
  private ziroModalService = inject(ZiroModalService);
  private translateService = inject(TranslateService);
  private phoneResource = inject(PhoneResource);
  private directoryNumberResource = inject(DirectoryNumberResource);
  private toastService = inject(ToastService);
  private snrProfileResource = inject(SnrProfileResource);
  private router = inject(Router);
  private searchFieldConfigContext = inject(SearchFieldConfigContext);
  private currentClusterContext = inject(CurrentClusterContext);
  private microsoftTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private microsoft365UserResource = inject(Microsoft365UserResource);
  private siteSummaryContext = inject(SiteSummaryContext);
  private searchExtensionMobilityProfilesResource = inject(SearchExtensionMobilityProfilesResource);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private voicemailResource = inject(VoicemailResource);
  private zpmUserPhotoContext = inject(ZpmUserPhotoContext);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private microsoftCallQueueSearchResource = inject(MicrosoftCallQueueSearchResource);
  private _emergencyLocationSearchResource = inject(EmergencyLocationSearchResource);
  private _autoAttendantsSearchResource = inject(MicrosoftAutoAttendantsSearchResource);

  zpmResultsLoadingState: ZpmResultsLoadingState = {
    microsoftEndUserResultsLoading: signal(true),
    callQueueResultsLoading: signal(true),
    emergencyLocationResultsLoading: signal(true),
    autoAttendantResultsLoading: signal(true),
  };
  isTeamsUserManagementAllowed = false;
  isCallQueueManagementAllowed = false;
  isEmergencyLocationManagementAllowed = false;
  isAutoAttendantManagementAllowed = false;

  userPhotosLoading = signal(false);

  zpmResultsLoading = signal(true);
  getUserInitialsFromDisplayName = getUserInitialsFromDisplayName;

  isHosted = false;
  isLoading = true;
  isLoadingLineUri = true;
  ZiroStackedIcons = ZiroStackedIcons;
  ziroIcons = ZiroIcons;
  zpcTotalResults: number;
  zpmTotalResults: number;
  query: string;
  snrResults: SnrProfileResult[] = [];
  cucmEndUserResults: UiEndUserResult[] = [];
  directoryNumberResults: DirectoryNumberResult[] = [];
  phoneResults: PhoneResult[] = [];
  distributionListResults: DistributionListResult[] = [];
  voicemailResults: VoicemailResult[] = [];
  extensionMobilityProfiles: ExtensionMobilityResult[] = [];
  devicePoolSites: DevicePoolSites = {};
  microsoftEndUserResults: MicrosoftUserUiResult[] = [];
  callQueueResults: MicrosoftTeamsCallQueue[] = [];
  emergencyLocationResults: LocationPlaceSearchResult[] = [];
  autoAttendantResults: MicrosoftTeamsAutoAttendant[] = [];

  private _subscriptions = new Subscription();
  private _siteSummary: SiteSummary;
  private _tenantId: string;

  Number = Number;

  private static _getPhoneTypeLabel(result: PhoneResult): string {
    switch (result.ref.model) {
      case KnownPhoneModels.ANDROID: {
        return 'tkey;shared.model.android.text';
      }
      case KnownPhoneModels.IPHONE: {
        return 'tkey;shared.model.iphone.text';
      }
      case KnownPhoneModels.CIPC: {
        return 'tkey;shared.model.cipc.text';
      }
      case KnownPhoneModels.TABLET: {
        return 'tkey;shared.model.tablet.text';
      }
      case KnownPhoneModels.IM_SOFTPHONE: {
        return 'tkey;shared.model.imsoftphone.text';
      }
      default: {
        return 'tkey;shared.model.deskphone.text';
      }
    }
  }

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;search.header',
      },
    ]);

    const globalPropsSub = this.globalPropertiesContext.state$.pipe(take(1)).subscribe((globalProperties) => {
      this.isHosted = globalProperties.hostedEnabled;
    });
    this._subscriptions.add(globalPropsSub);

    this.query = decodeURI(
      this.route.snapshot.queryParamMap.get('query') ? this.route.snapshot.queryParamMap.get('query').trim() : '',
    );
    this._getSearchConfig();

    const subscription = this.router.events.subscribe((event) => {
      // Without this check users cannot update the search query in the url manually
      if (event instanceof NavigationEnd) {
        this.isLoadingLineUri = true;
        this.isLoading = true;
        this.query = decodeURI(
          this.route.snapshot.queryParamMap.get('query') ? this.route.snapshot.queryParamMap.get('query').trim() : '',
        );
        this._getSearchConfig();
      }
    });
    this._subscriptions.add(subscription);

    const userPhotoSub = this.zpmUserPhotoContext.state$.subscribe(() => {
      const userPhotos = this.zpmUserPhotoContext.getUserPhotos();
      userPhotos.forEach((photo) => {
        const microsoftUserUiResult = this.microsoftEndUserResults.find(
          (result) => result.ref.id === photo.userPrincipalName,
        );
        if (microsoftUserUiResult) {
          microsoftUserUiResult.photo = photo.photoBase64;
        }
      });
    });
    this._subscriptions.add(userPhotoSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  get resultsSummaryParts(): { label: string; count: number }[] {
    const resultsSummary: { label: string; count: number }[] = [];
    if (this.isTeamsUserManagementAllowed) {
      resultsSummary.push({
        label: this.translateService.instant(
          this.microsoftEndUserResults.length === 1 ? 'tkey;search.users.singular.count' : 'tkey;search.users.count',
          { count: this.microsoftEndUserResults.length },
        ),
        count: this.microsoftEndUserResults?.length ?? 0,
      });
    }
    if (this.isCallQueueManagementAllowed) {
      resultsSummary.push({
        label: this.translateService.instant(
          this.callQueueResults.length === 1
            ? 'tkey;search.search.call_queues.singular.count'
            : 'tkey;search.search.call_queues.count',
          { count: this.callQueueResults.length },
        ),
        count: this.callQueueResults?.length ?? 0,
      });
    }
    if (this.isEmergencyLocationManagementAllowed) {
      resultsSummary.push({
        label: this.translateService.instant(
          this.emergencyLocationResults.length === 1
            ? 'tkey;search.search.emergency_locations.singular.count'
            : 'tkey;search.search.emergency_locations.count',
          { count: this.emergencyLocationResults.length },
        ),
        count: this.emergencyLocationResults?.length ?? 0,
      });
    }
    if (this.isAutoAttendantManagementAllowed) {
      resultsSummary.push({
        label: this.translateService.instant(
          this.autoAttendantResults.length === 1
            ? 'tkey;search.auto_attendants.singular.count'
            : 'tkey;search.auto_attendants.count',
          { count: this.autoAttendantResults.length },
        ),
        count: this.autoAttendantResults?.length ?? 0,
      });
    }
    return resultsSummary;
  }

  getCucmEndUserDescription(result: UiEndUserResult): string {
    return `<strong>${result.ref.username}</strong> | ${result.ref.firstName} ${result.ref.lastName}`;
  }

  usernameHasSpaces(result: UiEndUserResult): boolean {
    return /\s/.test(result.ref.username);
  }

  getCucmEndUserTooltip(result: UiEndUserResult): string {
    return result.tooltip
      ? result.tooltip
      : this.usernameHasSpaces(result)
        ? 'tkey;search.end_user_result.no_spaces_allowed_tooltip'
        : 'tkey;search.add.edit.button';
  }

  getPhoneDescription(result: PhoneResult): string {
    switch (result.ref.model) {
      case KnownPhoneModels.ANDROID:
      case KnownPhoneModels.IPHONE:
      case KnownPhoneModels.TABLET:
      case KnownPhoneModels.CIPC:
      case KnownPhoneModels.IM_SOFTPHONE: {
        return `<strong>${result.ref.name}</strong> | ${result.ref.description}`;
      }
      default: {
        return `${result.ref.model} - <strong>${result.ref.name}</strong> | ${result.ref.description}`;
      }
    }
  }

  getPhoneIcon(result: PhoneResult): ZiroIcons {
    switch (result.ref.model) {
      case KnownPhoneModels.ANDROID: {
        return this.ziroIcons.ANDROID_SMALL;
      }
      case KnownPhoneModels.IPHONE: {
        return this.ziroIcons.IPHONE_SMALL;
      }
      case KnownPhoneModels.CIPC: {
        return this.ziroIcons.CIPC_SOLID;
      }
      case KnownPhoneModels.TABLET: {
        return this.ziroIcons.TABLET_SMALL;
      }
      case KnownPhoneModels.IM_SOFTPHONE: {
        return this.ziroIcons.SOFTPHONE_SMALL;
      }
      default: {
        return this.ziroIcons.DESKPHONE_SMALL;
      }
    }
  }

  getPhoneEditLink(result: PhoneResult): string {
    switch (result.ref.model) {
      case KnownPhoneModels.ANDROID: {
        return `/user/${result.owner?.username}/android/${result.ref.id}`;
      }
      case KnownPhoneModels.IPHONE: {
        return `/user/${result.owner?.username}/iphone/${result.ref.id}`;
      }
      case KnownPhoneModels.CIPC: {
        return `/user/${result.owner?.username}/cipc/${result.ref.id}`;
      }
      case KnownPhoneModels.TABLET: {
        return `/user/${result.owner?.username}/tablet/${result.ref.id}`;
      }
      case KnownPhoneModels.IM_SOFTPHONE: {
        return `/user/${result.owner?.username}/im-softphone/${result.ref.id}`;
      }
      default: {
        if (result.owner) {
          return `/user/${result.owner?.username}/deskphone/${result.ref.id}`;
        }

        return `/public-phone/${result.ref.name}`;
      }
    }
  }

  onPhoneDeleteClicked(result: PhoneResult): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        title: this.translateService.instant('tkey;userdetail.phone.delete.confirmation.modal.header'),
        promptBody: this.translateService.instant('tkey;userdetail.phone.delete.confirmation.modal.text', {
          phoneName: result.ref.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              const auditTags = [];
              if (result.owner) {
                auditTags.push({
                  name: 'owner',
                  value: result.owner?.username,
                });
              }

              return this.phoneResource
                .delete(result.ref.id, result.ref.serverId.toString(), auditTags)
                .pipe(map(() => true));
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.toastService.pushDeleteToast(ZiroSearchComponent._getPhoneTypeLabel(result), result.ref.name);
          this.phoneResults = this.phoneResults.filter((phoneResult: PhoneResult) => {
            return phoneResult.ref.id !== result.ref.id;
          });
          this._setTotalZpcResults();
        }
      });
  }

  getDnDescription(result: DirectoryNumberResult): string {
    return `<strong>${result.ref.extension}</strong> | ${result.routePartition} | ${result.ref.description}`;
  }

  onDnDeleteClicked(result: DirectoryNumberResult): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        promptBody: this.translateService.instant('tkey;search.directory.number.delete.voicemail.warning.text', {
          extension: result.ref.extension,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._onDnDeleteConfirmClicked(result);
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.toastService.pushDeleteToast(
            'tkey;site_management.site.section.directory_number.title',
            result.ref.extension,
          );
          this.directoryNumberResults = this.directoryNumberResults.filter(
            (directoryNumberResult: DirectoryNumberResult) => {
              return directoryNumberResult.ref.id !== result.ref.id;
            },
          );
          this.voicemailResults = this.voicemailResults.filter((voicemailResult: VoicemailResult) => {
            return voicemailResult.ref.extension !== result.ref.extension;
          });

          this._setTotalZpcResults();
        }
      });
  }

  getVoicemailDescription(result: VoicemailResult): string {
    return `<strong>${result.ref.alias}</strong> | ${result.ref.extension}`;
  }

  onVoicemailDeleteClicked(result: VoicemailResult): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        promptBody: this.translateService.instant('tkey;search.voicemail.delete.warning.text', {
          alias: result.ref.alias,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._onVmDeleteConfirmClicked(result);
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.toastService.pushDeleteToast('tkey;shared.service.voicemail.text', result.ref.alias);
          this.voicemailResults = this.voicemailResults.filter((vmr: VoicemailResult) => {
            return vmr.ref.id !== result.ref.id;
          });

          this._setTotalZpcResults();
        }
      });
  }

  getDistributionListDescription(result: DistributionListResult): string {
    return `<strong>${result.alias}</strong> | ${result.displayName}`;
  }

  getSnrDescription(result: SnrProfileResult): string {
    return `<strong>${result.ref.name}</strong> | ${result.ref.description}`;
  }

  onSnrDeleteClicked(result: SnrProfileResult): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        title: this.translateService.instant('tkey;userdetail.snr.delete.confirmation.modal.header'),
        promptBody: this.translateService.instant('tkey;userdetail.snr.delete.confirmation.modal.text', {
          snrName: result.ref.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this.snrProfileResource.deleteSnrProfile(result.ref.id, result.ref.serverId).pipe(map(() => true));
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.toastService.pushDeleteToast('tkey;shared.service.snr.text', result.ref.name);
          this.snrResults = this.snrResults.filter((snr: SnrProfileResult) => {
            return snr.ref.id !== result.ref.id;
          });

          this._setTotalZpcResults();
        }
      });
  }

  onExtensionMobilityProfileDeleteClicked(result: ExtensionMobilityResult): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: encodeURIComponent('text-danger'),
        title: this.translateService.instant(
          'tkey;userdetail.extension_mobility_profile.delete.confirmation.modal.header',
        ),
        promptBody: this.translateService.instant(
          'tkey;userdetail.extension_mobility_profile.delete.confirmation.modal.text',
          {
            udpName: result.ref.name,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this.searchExtensionMobilityProfilesResource
                .deleteExtensionMobilityProfile(result.ref.id, result.ref.serverId)
                .pipe(map(() => true));
            },
          },
        ],
      },
    };

    this.ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.toastService.pushDeleteToast('tkey;shared.service.extensionmobility.text', result.ref.name);
          this.extensionMobilityProfiles = this.extensionMobilityProfiles.filter(
            (extensionMobilityProfile: ExtensionMobilityResult) => {
              return extensionMobilityProfile.ref.id !== result.ref.id;
            },
          );
          this._setTotalZpcResults();
        }
      });
  }

  getPinResetLink(result: VoicemailResult): string {
    return '/unity-servers/' + result.ref.serverId + '/voicemails/' + result.ref.id + '/pin-reset';
  }

  getExtensionMobilityProfileDescription(result: ExtensionMobilityResult): string {
    return `${result.ref.model} - <strong> ${result.ref.name} </strong> | ${result.ref.description}`;
  }

  getPhoneEditLinkState(result: PhoneResult | SnrProfileResult): EditLinkState {
    const editLinkState: EditLinkState = {
      isDisabled: false,
      disabledTooltip: '',
    };

    editLinkState.isDisabled = this._getPhoneEditLinkIsDisabled(result);
    editLinkState.disabledTooltip = this._getPhoneEditLinkTooltip(result);

    return editLinkState;
  }

  handleResultClick(isGuestAccount: boolean, id: string): void {
    if (isGuestAccount) {
      return;
    }
    this.router.navigate([`/helpdesk/tenants/${this._tenantId}/360-view/${id}`]);
  }

  handleCallQueueClick(callQueueId: string): void {
    this.router.navigate([`/call-queue-management/tenants/${this._tenantId}/teams-call-queues/${callQueueId}`]);
  }

  getCallQueueResultAsMicrosoftUser(callQueue: MicrosoftTeamsCallQueue): MicrosoftUserUiResult {
    if (!callQueue) {
      return {
        ref: {
          id: '',
          displayName: '',
          url: '',
        },
        lineUri: null,
        lineUriExtension: null,
        alternateLineUris: [],
        photo: null,
      };
    }

    return {
      ref: {
        id: callQueue.callQueueId ?? '',
        displayName: callQueue.name ?? '',
        url: '',
      },
      lineUri: null,
      lineUriExtension: null,
      alternateLineUris: [],
      photo: null,
    };
  }

  handleLocationClick(link: string): void {
    this.router.navigate([link]);
  }

  getLocationAsMicrosoft365UserRef(location: LocationPlaceSearchResult): Microsoft365UserRef {
    return {
      id: location.address,
      displayName: location.name,
      url: '',
    };
  }

  handleAutoAttendantClick(autoAttendantId: string): void {
    this.router.navigate([
      `/auto-attendant-management/tenants/${this._tenantId}/teams-auto-attendants/${autoAttendantId}`,
    ]);
  }

  getAutoAttendantResultAsMicrosoftUser(autoAttendant: MicrosoftTeamsAutoAttendant): MicrosoftUserUiResult {
    if (!autoAttendant) {
      return {
        ref: {
          id: '',
          displayName: '',
          url: '',
        },
        lineUri: null,
        lineUriExtension: null,
        alternateLineUris: [],
        photo: null,
      };
    }

    return {
      ref: {
        id: autoAttendant.id ?? '',
        displayName: autoAttendant.name ?? '',
        url: '',
      },
      lineUri: null,
      lineUriExtension: null,
      alternateLineUris: [],
      photo: null,
    };
  }

  private _convertModelToDescription(model: PhoneDescriptions): ServiceTypes {
    switch (model) {
      case 'Cisco Jabber for Tablet':
        return ServiceTypes.TABLET;
      case 'Cisco Dual Mode for Android':
        return ServiceTypes.ANDROID;
      case 'Cisco Dual Mode for iPhone':
        return ServiceTypes.IPHONE;
      case 'Cisco Unified Client Services Framework':
        return ServiceTypes.SOFTPHONE;
      case 'Cisco IP Communicator':
        return ServiceTypes.CIPC;
      case 'Remote Destination Profile':
        return ServiceTypes.SNR;
      default:
        return ServiceTypes.DESKPHONE;
    }
  }

  private _isPhoneOrServiceInDevicePool(result: PhoneResult | SnrProfileResult): boolean {
    const matchingCluster = this._siteSummary.clusters
      .filter((cluster) => cluster.cucmServerId === result.ref.serverId)
      .find((cluster: ClusterResult) => {
        const matchingSite = cluster.sites.find((site: SiteResult) => {
          const matchingDevicePool = site.devicePools.find((devicePool: DevicePoolResult) => {
            if ('model' in result && result.model === 'Remote Destination Profile') {
              const snrResult = result as SnrProfileResult;
              return (
                devicePool.serviceName === this._convertModelToDescription(snrResult.model as PhoneDescriptions) &&
                devicePool.devicePool === result.ref.devicePool
              );
            } else {
              const phoneResult = result as PhoneResult;
              return (
                devicePool.serviceName ===
                  this._convertModelToDescription(phoneResult.ref.model as PhoneDescriptions) &&
                devicePool.devicePool === result.ref.devicePool
              );
            }
          });
          return !!matchingDevicePool;
        });
        return !!matchingSite;
      });
    return !!matchingCluster;
  }

  private _getPhoneEditLinkTooltip(result: PhoneResult | SnrProfileResult): string {
    if ('model' in result && result.model === 'Remote Destination Profile') {
      result = result as SnrProfileResult;
      return !this._isPhoneOrServiceInDevicePool(result) ? 'tkey;helpdesk.search.device.tooltip.disabled' : '';
    } else {
      result = result as PhoneResult;
      if (
        !result.owner &&
        [
          KnownPhoneModels.ANDROID,
          KnownPhoneModels.IPHONE,
          KnownPhoneModels.CIPC,
          KnownPhoneModels.TABLET,
          KnownPhoneModels.IM_SOFTPHONE,
        ].includes(result.ref.model as KnownPhoneModels)
      ) {
        return 'tkey;search.devices.no_owner_tooltip';
      } else if (!this._isPhoneOrServiceInDevicePool(result)) {
        return 'tkey;helpdesk.search.device.tooltip.disabled';
      } else {
        return '';
      }
    }
  }

  private _getPhoneEditLinkIsDisabled(result: PhoneResult | SnrProfileResult): boolean {
    if ('model' in result && result.model === 'Remote Destination Profile') {
      result = result as SnrProfileResult;
      return !this._isPhoneOrServiceInDevicePool(result);
    } else {
      result = result as PhoneResult;
      switch (result.ref.model) {
        case KnownPhoneModels.ANDROID:
        case KnownPhoneModels.IPHONE:
        case KnownPhoneModels.CIPC:
        case KnownPhoneModels.TABLET:
        case KnownPhoneModels.IM_SOFTPHONE: {
          return !result.owner || !this._isPhoneOrServiceInDevicePool(result);
        }
        default: {
          return !this._isPhoneOrServiceInDevicePool(result);
        }
      }
    }
  }

  private _clearResults(): void {
    this.snrResults = [];
    this.cucmEndUserResults = [];
    this.directoryNumberResults = [];
    this.phoneResults = [];
    this.distributionListResults = [];
    this.voicemailResults = [];
    this.extensionMobilityProfiles = [];
    this.microsoftEndUserResults = [];
    this.callQueueResults = [];
    this.emergencyLocationResults = [];
    this.zpmResultsLoading.set(true);
    this.zpmResultsLoadingState.microsoftEndUserResultsLoading.set(true);
    this.zpmResultsLoadingState.callQueueResultsLoading.set(true);
    this.zpmResultsLoadingState.emergencyLocationResultsLoading.set(true);
    this.zpmResultsLoadingState.autoAttendantResultsLoading.set(true);
    this.userPhotosLoading.set(false);
  }

  private _getSearchConfig(): void {
    if (!this.query || this.query.length < 3) {
      this._clearResults();
      this.isLoading = false;
      return;
    }

    const contextSubs = combineLatest([this.searchFieldConfigContext.state$, this.authenticationContext.state$])
      .pipe(
        switchMap(([searchFieldConfig, currentUser]) => {
          if (this.isHosted) {
            return this._microsoftTenantsStateContext.cachedTenantState$.pipe(
              filter((cachedTenant) => !!cachedTenant?.tenantId),
              take(1),
              switchMap((cachedTenant) => {
                this._tenantId = cachedTenant.tenantId.toString();
                this._setZpmUserPrivilegeFlags(this._tenantId, currentUser);
                this.isLoading = false;
                return this._getQueries(searchFieldConfig, currentUser, this._tenantId);
              }),
            );
          } else if (
            searchFieldConfig &&
            (searchFieldConfig.cucmSearchEnabled || searchFieldConfig.unitySearchEnabled)
          ) {
            return this.currentClusterContext.state$.pipe(
              take(1),
              switchMap(() => {
                return this._getQueries(searchFieldConfig, currentUser);
              }),
            );
          }

          return this._getQueries(searchFieldConfig, currentUser);
        }),
      )
      .subscribe(() => {
        if (this.isHosted) {
          this._setTotalZpmResults();
        } else {
          this._setTotalZpcResults();
          this.isLoading = false;
        }
      });
    this._subscriptions.add(contextSubs);
  }

  private _getQueries(
    searchFieldConfig: SearchFieldConfig,
    currentUser: CurrentUser,
    tenantId?: string,
  ): Observable<void[]> {
    const queries = [];

    if (!this.isHosted) {
      if (searchFieldConfig.cucmSearchEnabled) {
        queries.push(this._cucmSearch(currentUser));
      }
      if (searchFieldConfig.unitySearchEnabled) {
        queries.push(this._unitySearch(currentUser));
      }
    }

    if (this.isHosted) {
      queries.push(this._msSearch(tenantId));
    }

    return queries.length ? forkJoin(queries) : of(null);
  }

  private _hasPermissionsOnAnyCluster(cucmServerIds: number[], siteSummary: SiteSummary): boolean {
    return !!cucmServerIds.find((id: number) => {
      const clusters = siteSummary.clusters.filter((clusterResult: ClusterResult) => clusterResult.cucmServerId === id);
      return clusters.some((cluster) => cluster.sites.some((site) => site.hasPermission));
    });
  }

  private _cucmSearch(currentUser: CurrentUser): Observable<void> {
    const cucmSearches: Observable<
      | SnrProfileResult[]
      | EndUserResult[]
      | PhoneResult[]
      | SiteSummary
      | DirectoryNumberResult[]
      | ExtensionMobilityResult[]
    >[] = [
      this.searchResource.searchSnrProfiles(this.query),
      this.endUserSearchResource.searchByQ(this.query),
      this.searchPhoneResource.get({ q: this.query }),
      this.siteSummaryContext.state$.pipe(take(1)),
      this.searchExtensionMobilityProfilesResource.getExtensionMobilityProfiles({ q: this.query }),
    ];

    if (
      this.authenticationContext.userIsAtLeast(currentUser, this.isHosted ? Role.S8_HELPDESK : Role.S8_GLOBAL_HELPDESK)
    ) {
      cucmSearches.push(this.searchDirectoryNumberResource.get({ q: this.query }));
    }
    return new Observable((subscriber: Subscriber<void>) => {
      forkJoin(cucmSearches).subscribe(
        ([snrProfileResults, endUserResults, phoneResults, siteSummary, extensionMobilityResults, dnResults]: [
          SnrProfileResult[],
          EndUserResult[],
          PhoneResult[],
          SiteSummary,
          ExtensionMobilityResult[],
          DirectoryNumberResult[],
        ]) => {
          // Create request to lookup the sites for all the device pools on the phones & snrs
          this._siteSummary = siteSummary;
          const flattened = [].concat(snrProfileResults, phoneResults);
          const devicePools = uniq(flattened.map((result: SnrProfileResult | PhoneResult) => result.ref.devicePool));

          this.snrResults = snrProfileResults;

          const groupedEndUserResults = groupBy(endUserResults, (result: EndUserResult) => result.ref.username);
          const cucmEndUserResults = uniqBy(endUserResults, (result: EndUserResult) => {
            return result.ref.username;
          }).sort((user1: EndUserResult, user2: EndUserResult) => {
            return user1.ref.username.localeCompare(user2.ref.username);
          });

          this.cucmEndUserResults = cucmEndUserResults.map((endUser: EndUserResult) => {
            const cucmServers = groupedEndUserResults[endUser.ref.username].map(
              (groupedEndUser: EndUserResult) => groupedEndUser.ref.serverId,
            );
            const hasPermissionsOnAnyCluster = this._hasPermissionsOnAnyCluster(cucmServers, this._siteSummary);
            const userTooltipWhenClustersExist: string =
              endUser.type === 'LDAP_ACTIVE' && !hasPermissionsOnAnyCluster
                ? 'tkey;search.end_user_result.ldap_sync_in_progress.tooltip'
                : !hasPermissionsOnAnyCluster
                  ? 'tkey;search.devices.no_permissions_tooltip'
                  : '';
            return {
              ...endUser,
              isSyncInProgress:
                siteSummary?.clusters.length > 0
                  ? endUser.type === 'LDAP_ACTIVE' && !hasPermissionsOnAnyCluster
                  : false,
              isDisabled: siteSummary?.clusters.length > 0 ? !hasPermissionsOnAnyCluster : false,
              tooltip: siteSummary?.clusters.length > 0 ? userTooltipWhenClustersExist : '',
            };
          });

          this.phoneResults = phoneResults
            .filter(
              (result: PhoneResult) =>
                result.ref.model !== KnownPhoneModels.CTI_PORT &&
                result.ref.model !== KnownPhoneModels.CTI_REMOTE_DEVICE &&
                result.ref.model !== KnownPhoneModels.SPARK_REMOTE_DEVICE,
            )
            .sort((phone1: PhoneResult, phone2: PhoneResult) => {
              return phone1.ref.name.localeCompare(phone2.ref.name);
            });

          this.extensionMobilityProfiles = extensionMobilityResults || [];
          this.directoryNumberResults = dnResults || [];

          if (this._siteSummary.clusters.length) {
            // Group device pool sites by cucm server id as they can share the same name cross cluster
            // Individual phone results have the cucm server id to get the matching site from site search results
            this._siteSummary.clusters.forEach((clusterResult) => {
              if (!this.devicePoolSites[clusterResult.cucmServerId]) {
                this.devicePoolSites[clusterResult.cucmServerId] = [];
              }
              clusterResult.sites.forEach((site) => {
                devicePools.forEach((devicePool: string) => {
                  const match = site.devicePools.find((pool) => pool.devicePool === devicePool);
                  if (match) {
                    this.devicePoolSites[clusterResult.cucmServerId].push(site);
                  }
                });
              });
            });
          }

          subscriber.next(null);
          subscriber.complete();
        },
      );
    });
  }

  private _unitySearch(currentUser: CurrentUser): Observable<void> {
    const unitySearches: Observable<VoicemailResult[] | DistributionListResult[]>[] = [
      this.voicemailSearchResource.searchByQ(this.query),
    ];

    if (
      this.authenticationContext.userIsAtLeast(currentUser, this.isHosted ? Role.S8_HELPDESK : Role.S8_GLOBAL_HELPDESK)
    ) {
      unitySearches.push(this.searchResource.searchDistributionLists(this.query));
    }

    return forkJoin(unitySearches).pipe(
      map((data: [VoicemailResult[], DistributionListResult[]]) => {
        this.voicemailResults = data[0];
        this.distributionListResults = data[1] || [];
      }),
    );
  }

  private _setZpmUserPrivilegeFlags(tenantId: string, currentUser: CurrentUser): void {
    if (tenantId) {
      this.isTeamsUserManagementAllowed = this.authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.TEAMS_USER_MANAGEMENT,
        tenantId,
      );
      this.isCallQueueManagementAllowed = this.authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.CALL_QUEUE_MANAGEMENT,
        tenantId,
      );
      this.isEmergencyLocationManagementAllowed = this.authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT,
        tenantId,
      );
      this.isAutoAttendantManagementAllowed = this.authenticationContext.userHasPrivilege(
        currentUser,
        ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT,
        tenantId,
      );
    }
  }

  private _msSearch(tenantId: string): Observable<void> {
    this.zpmResultsLoadingState.microsoftEndUserResultsLoading.update(() => true);
    this.zpmResultsLoadingState.callQueueResultsLoading.update(() => true);
    this.zpmResultsLoadingState.emergencyLocationResultsLoading.update(() => true);
    this.zpmResultsLoadingState.autoAttendantResultsLoading.update(() => true);
    this.zpmResultsLoading.set(true);

    const isNumberSearch = /^\+?[0-9() -]*$/.test(this.query);

    const teamsResults$: Observable<MicrosoftTeamsUserResult[] | null> = this.isTeamsUserManagementAllowed
      ? this.microsoftTeamsUserSearchResource.search(tenantId, this.query, true).pipe(startWith(null))
      : of(null);

    const microsoft365Results$: Observable<Microsoft365UserResult[] | null> =
      !isNumberSearch && this.isTeamsUserManagementAllowed
        ? this.microsoft365UserResource.search(tenantId, this.query).pipe(
            tap((microsoft365Results: Microsoft365UserResult[]) => {
              this.microsoftEndUserResults = microsoft365Results
                .map(
                  (result): MicrosoftUserUiResult => ({
                    ref: result.microsoft365UserRef,
                    lineUri: null, // lineUri and extension will be populated later
                    lineUriExtension: null,
                    alternateLineUris: [],
                    photo: null,
                  }),
                )
                .sort((a, b) => a.ref.id?.localeCompare(b.ref.id));
              this.userPhotosLoading.set(true);
              this.zpmUserPhotoContext
                .initUserPhotos(
                  tenantId,
                  this.microsoftEndUserResults.map((result) => result.ref.id),
                )
                .pipe(finalize(() => this.userPhotosLoading.set(false)))
                .subscribe();
            }),
            startWith(null),
          )
        : of(null);

    const callQueueResults$: Observable<MicrosoftTeamsCallQueue[] | null> = this.isCallQueueManagementAllowed
      ? this.microsoftCallQueueSearchResource.search(tenantId, this.query).pipe(startWith(null))
      : of(null);

    const emergencyLocationResults$: Observable<EmergencyLocationsSearchResults | null> = this
      .isEmergencyLocationManagementAllowed
      ? this._emergencyLocationSearchResource.searchLocations(tenantId, this.query).pipe(startWith(null))
      : of(null);

    const autoAttendantsResults$: Observable<MicrosoftTeamsAutoAttendant[] | null> = this
      .isAutoAttendantManagementAllowed
      ? this._autoAttendantsSearchResource.search(tenantId, this.query).pipe(startWith(null))
      : of(null);

    return combineLatest([
      teamsResults$,
      microsoft365Results$,
      callQueueResults$,
      emergencyLocationResults$,
      autoAttendantsResults$,
    ]).pipe(
      tap(([teamsResults, microsoft365Results, callQueueResults, emergencyLocationResults, autoAttendantsResults]) => {
        if (teamsResults !== null && (isNumberSearch || microsoft365Results !== null)) {
          if (isNumberSearch) {
            this.microsoftEndUserResults = teamsResults
              .map(
                (result: MicrosoftTeamsUserResult): MicrosoftUserUiResult => ({
                  ref: result.ref,
                  lineUri: result.lineUri,
                  lineUriExtension: result.lineUriExtension,
                  alternateLineUris: result.alternateLineUris,
                  photo: null,
                }),
              )
              .sort((a: MicrosoftUserUiResult, b: MicrosoftUserUiResult) => a.ref.id?.localeCompare(b.ref.id));
          }
          this.microsoftEndUserResults.forEach((userResult: MicrosoftUserUiResult) => {
            const matchingTeamsResult = teamsResults.find(
              (teamResult: MicrosoftTeamsUserResult) => teamResult.ref.id === userResult.ref.id,
            );
            if (matchingTeamsResult) {
              userResult.lineUri = matchingTeamsResult.lineUri;
              userResult.lineUriExtension = matchingTeamsResult.lineUriExtension;
              userResult.alternateLineUris = matchingTeamsResult.alternateLineUris;
            }
          });
          this.isLoadingLineUri = false;
          this.zpmResultsLoadingState.microsoftEndUserResultsLoading.update(() => false);
        }

        if (callQueueResults !== null) {
          this.callQueueResults = callQueueResults;
          this.zpmResultsLoadingState.callQueueResultsLoading.update(() => false);
        }

        if (emergencyLocationResults !== null) {
          const locations: LocationPlaceSearchResult[] = [];

          emergencyLocationResults.emergencyLocations.forEach((location) => {
            locations.push({
              id: location.ref.id,
              name: location.name,
              address: location.address,
              location: '',
              link: `/emergency-calling-management/tenants/${tenantId}/emergency-locations/${location.ref.id}`,
            });
          });

          emergencyLocationResults.places.forEach((place) => {
            locations.push({
              id: place.ref.id,
              name: place.name,
              address: place.address,
              location: place.emergencyLocationName,
              link: `/emergency-calling-management/tenants/${tenantId}/emergency-locations/${place.ref.civicAddressId}/places/${place.ref.id}`,
            });
          });

          this.emergencyLocationResults = locations.sort((a, b) => {
            if (a.location && b.location) {
              return a.location.localeCompare(b.location);
            } else if (a.location) {
              return 1;
            } else if (b.location) {
              return -1;
            } else {
              return a.name.localeCompare(b.name);
            }
          });

          this.zpmResultsLoadingState.emergencyLocationResultsLoading.update(() => false);
        }

        if (autoAttendantsResults !== null) {
          this.autoAttendantResults = autoAttendantsResults;
          this.zpmResultsLoadingState.autoAttendantResultsLoading.update(() => false);
        }
      }),
      map((): void => void 0),
      finalize(() => {
        this.isLoadingLineUri = false;
        this.zpmResultsLoadingState.microsoftEndUserResultsLoading.update(() => false);
        this.zpmResultsLoadingState.callQueueResultsLoading.update(() => false);
        this.zpmResultsLoadingState.emergencyLocationResultsLoading.update(() => false);
        this.zpmResultsLoadingState.autoAttendantResultsLoading.update(() => false);
        this.zpmResultsLoading.set(false);
      }),
    );
  }

  private _setTotalZpcResults(): void {
    this.zpcTotalResults =
      this.snrResults.length +
      this.cucmEndUserResults.length +
      this.directoryNumberResults.length +
      this.phoneResults.length +
      this.distributionListResults.length +
      this.voicemailResults.length +
      this.extensionMobilityProfiles.length;
  }

  private _setTotalZpmResults(): void {
    this.zpmTotalResults =
      this.microsoftEndUserResults.length +
      this.callQueueResults.length +
      this.emergencyLocationResults.length +
      this.autoAttendantResults.length;
    this.zpmResultsLoading.set(false);
  }

  private _onDnDeleteConfirmClicked(result: DirectoryNumberResult): Observable<boolean> {
    // search for vm first
    return new Observable((subscriber: Subscriber<boolean>) => {
      this.voicemailSearchResource
        .searchByExtension(result.ref.extension)
        .pipe(
          tap((vmResult: VoicemailResult[]) => {
            if (vmResult.length) {
              this.endUserSearchResource.searchByQ(vmResult[0].ref.extension).subscribe((endUserSearchResult) => {
                const endUserResult = endUserSearchResult[0];
                this._callVoicemailServiceDelete(vmResult[0], endUserResult)
                  .pipe(
                    tap(() => {
                      this.directoryNumberResource.delete(result.ref.id, result.ref.serverId).subscribe(() => {
                        this.toastService.pushDeleteToast('tkey;shared.service.voicemail.text', vmResult[0].ref.alias);
                        subscriber.next(true);
                        subscriber.complete();
                      });
                    }),
                    catchError((response) => {
                      subscriber.next(false);
                      subscriber.complete();
                      return throwError(() => response);
                    }),
                  )
                  .subscribe();
              });
            } else {
              this.directoryNumberResource.delete(result.ref.id, result.ref.serverId).subscribe({
                next: () => {
                  subscriber.next(true);
                  subscriber.complete();
                },
                error: (err) => {
                  subscriber.next(false);
                  subscriber.complete();
                  return throwError(() => err);
                },
              });
            }
          }),
          catchError((response) => {
            subscriber.next(false);
            subscriber.complete();
            return throwError(() => response);
          }),
        )
        .subscribe();
    });
  }

  private _onVmDeleteConfirmClicked(vmResult: VoicemailResult): Observable<boolean> {
    return new Observable((subscriber: Subscriber<boolean>) => {
      this.endUserSearchResource.searchByQ(vmResult.ref.extension).subscribe((results: EndUserResult[]) => {
        this._callVoicemailServiceDelete(vmResult, results[0])
          .pipe(
            tap(() => {
              subscriber.next(true);
              subscriber.complete();
            }),
            catchError((response) => {
              subscriber.next(false);
              subscriber.complete();
              return throwError(() => response);
            }),
          )
          .subscribe();
      });
    });
  }

  private _callVoicemailServiceDelete(vmResult: VoicemailResult, endUserResult: EndUserResult): Observable<void> {
    const auditTags: Nvp[] = endUserResult ? [{ name: 'username', value: endUserResult.ref.username }] : null;
    return this.voicemailResource.delete(vmResult.ref.serverId, vmResult.ref.id, auditTags);
  }
}
